import { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  AlertTriangle,
  Check,
  Loader2,
  Plus,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { Combobox } from "../components/forms/Combobox";
import { DateOfBirthPicker } from "../components/forms/DateOfBirthPicker";
import { SegmentedControl } from "../components/forms/SegmentedControl";
import { MonthYearPicker } from "../components/forms/MonthYearPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full h-12 px-4 rounded-xl border border-[var(--light-border)] bg-white text-[15px] text-[var(--charcoal)] placeholder:text-[var(--text-muted)]/70 transition-all focus:outline-none focus:border-[var(--lime)] focus:ring-4 focus:ring-[var(--lime)]/20";
const selectCls =
  inputCls +
  " appearance-none cursor-pointer pr-10 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23475569%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22><polyline points=%226 9 12 15 18 9%22/></svg>')] bg-no-repeat bg-[right_0.875rem_center]";

const Label = ({ children, error }) => (
  <label className="block text-[15px] font-semibold text-[var(--charcoal)] mb-2">
    {children}
    {error && (
      <span className="ml-2 text-xs font-medium text-red-600">{error}</span>
    )}
  </label>
);

/**
 * Magic-link landing page. Exchanges the `?token=...` query param for the
 * caller's record, lets them edit, then PATCHes back with the same token.
 *
 * Token is single-use: when the user submits, the backend marks the token
 * spent. So we redirect to the success page after PATCH and DO NOT keep the
 * token in the URL on success.
 */
export default function AccountUpdatePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audience, setAudience] = useState(null);
  const [originalEmail, setOriginalEmail] = useState("");
  const [form, setForm] = useState(null);
  const [options, setOptions] = useState({
    countries: [],
    ethnicities: [],
    genders: [],
    conditions: [],
    popular_conditions: [],
  });
  const [saving, setSaving] = useState(false);

  // Load record + form options once
  useEffect(() => {
    if (!token) {
      setError({
        code: 400,
        message:
          "No update token was supplied. Please request a fresh link from the email we sent you.",
      });
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [meRes, optsRes] = await Promise.all([
          axios.get(`${API}/account/me`, { params: { token } }),
          axios.get(`${API}/patients/form-options`),
        ]);
        if (cancelled) return;
        setAudience(meRes.data.audience);
        // Normalise conditions so missing optional fields are "" (UI relies on
        // them) but PRESERVE the per-condition id so backend admin views stay
        // consistent across edits.
        const rec = { ...meRes.data.record };
        if (Array.isArray(rec.conditions)) {
          rec.conditions = rec.conditions.map((c) => ({
            id: c.id,
            condition_label: c.condition_label || "",
            specify: c.specify || "",
            diagnosis_date: c.diagnosis_date || "",
            severity: c.severity || "",
            treatment: c.treatment || "",
            other_info: c.other_info || "",
          }));
        }
        setForm(rec);
        setOriginalEmail(rec.email || "");
        setOptions({
          countries: optsRes.data.countries || [],
          ethnicities: optsRes.data.ethnicities || [],
          genders: optsRes.data.genders || [],
          conditions: optsRes.data.conditions || [],
          popular_conditions: optsRes.data.popular_conditions || [],
        });
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.status || 500;
        const msg =
          err?.response?.data?.detail ||
          "Something went wrong loading your record. Please request a new link.";
        setError({ code, message: msg });
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const setField = (key, value) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  const setCondition = (idx, key, value) =>
    setForm((f) => {
      if (!f) return f;
      const conditions = (f.conditions || []).map((c, i) =>
        i === idx ? { ...c, [key]: value } : c,
      );
      return { ...f, conditions };
    });

  const addCondition = () =>
    setForm((f) =>
      f
        ? {
            ...f,
            conditions: [
              ...(f.conditions || []),
              {
                id:
                  typeof crypto !== "undefined" && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                condition_label: "",
                specify: "",
                diagnosis_date: "",
                severity: "",
                treatment: "",
                other_info: "",
              },
            ],
          }
        : f,
    );

  const removeCondition = (idx) =>
    setForm((f) => {
      if (!f) return f;
      const list = f.conditions || [];
      if (list.length <= 1) return f;
      return { ...f, conditions: list.filter((_, i) => i !== idx) };
    });

  const conditionChoices = useMemo(() => {
    const list = options.conditions || [];
    return list.includes("Other") ? list : [...list, "Other"];
  }, [options.conditions]);

  const countryOpts = useMemo(
    () =>
      (options.countries || []).map((c) => ({
        value: c.name,
        label: c.name,
      })),
    [options.countries],
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form) return;
    // Build allowed-fields payload
    const editable =
      audience === "patient"
        ? [
            "first_name",
            "last_name",
            "email",
            "phone_prefix",
            "phone_number",
            "date_of_birth",
            "gender",
            "ethnicity",
            "ethnicity_specify",
            "country",
            "postal_code",
            "conditions",
          ]
        : [
            "first_name",
            "last_name",
            "email",
            "phone_prefix",
            "phone_number",
            "date_of_birth",
            "country",
            "postal_code",
            "care_recipients",
          ];
    const fields = {};
    for (const k of editable) {
      if (k in form) fields[k] = form[k];
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/account/me`, { token, fields });
      const emailChanged = fields.email && fields.email !== originalEmail;
      toast.success("Your details have been updated.");
      navigate(`/account/updated${emailChanged ? "?email_changed=1" : ""}`, {
        replace: true,
      });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        toast.error(detail || "That email is already in use on another account.");
      } else if (status === 410) {
        toast.error(detail || "This link has expired or already been used.");
        setError({ code: 410, message: detail });
      } else {
        toast.error(
          typeof detail === "string"
            ? detail
            : "Could not save your changes. Please try again.",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 size={28} className="text-[var(--teal)] animate-spin" />
      </div>
    );
  }

  if (error) {
    return <LinkBrokenState code={error.code} message={error.message} />;
  }

  return (
    <div className="bg-white min-h-screen">
      {/* Hero — DARK */}
      <section className="bg-[var(--dark-bg)] grain-dark relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/12 blur-3xl pointer-events-none"
        />
        <div className="container-main py-12 md:py-14 relative z-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors mb-5"
          >
            <ArrowLeft size={14} />
            Back to home
          </Link>
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-3">
            <span className="w-6 h-px bg-[var(--lime)]" />
            Update your details
          </span>
          <h1 className="font-['Outfit'] font-bold text-white tracking-tight text-3xl md:text-4xl mb-2 leading-[1.1]">
            Hi {form.first_name}, here's what we have on file
          </h1>
          <p className="text-base text-white/80 leading-relaxed max-w-xl">
            Change anything below and hit save. This link is single-use, so
            once you save we'll close it.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 md:py-16">
        <div className="container-main max-w-3xl">
          <form
            onSubmit={onSubmit}
            className="bg-[#f1f5f9] rounded-2xl px-6 md:px-10 py-8 md:py-10 space-y-7"
            data-testid="account-update-form"
          >
            <h2 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-xl mb-2">
              Your contact details
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label>First name *</Label>
                <input
                  className={inputCls}
                  value={form.first_name || ""}
                  onChange={(e) => setField("first_name", e.target.value)}
                  required
                  data-testid="account-first-name"
                />
              </div>
              <div>
                <Label>Last name *</Label>
                <input
                  className={inputCls}
                  value={form.last_name || ""}
                  onChange={(e) => setField("last_name", e.target.value)}
                  required
                  data-testid="account-last-name"
                />
              </div>
            </div>

            <div>
              <Label>Email *</Label>
              <input
                type="email"
                className={inputCls}
                value={form.email || ""}
                onChange={(e) => setField("email", e.target.value)}
                required
                data-testid="account-email"
              />
              {form.email !== originalEmail && (
                <p className="text-[12.5px] text-[#475569] mt-2 leading-relaxed">
                  Changing your email will send a notice to your previous
                  address as a safety measure.
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div>
                <Label>Phone prefix *</Label>
                <input
                  className={inputCls}
                  value={form.phone_prefix || ""}
                  onChange={(e) => setField("phone_prefix", e.target.value)}
                  placeholder="+44"
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Mobile number *</Label>
                <input
                  className={inputCls}
                  value={form.phone_number || ""}
                  onChange={(e) => setField("phone_number", e.target.value)}
                  placeholder="7700900000"
                  inputMode="tel"
                  required
                />
              </div>
            </div>

            <div>
              <Label>Date of birth *</Label>
              <DateOfBirthPicker
                value={form.date_of_birth || ""}
                onChange={(v) => setField("date_of_birth", v)}
              />
            </div>

            {audience === "patient" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label>Gender *</Label>
                  <select
                    className={selectCls}
                    value={form.gender || ""}
                    onChange={(e) => setField("gender", e.target.value)}
                    required
                  >
                    <option value="">Select…</option>
                    {options.genders.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Ethnicity *</Label>
                  <select
                    className={selectCls}
                    value={form.ethnicity || ""}
                    onChange={(e) => setField("ethnicity", e.target.value)}
                    required
                  >
                    <option value="">Select…</option>
                    {options.ethnicities.map((e) => (
                      <option key={e} value={e}>
                        {e}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label>Country *</Label>
                <Combobox
                  options={countryOpts}
                  value={form.country || ""}
                  onChange={(v) => setField("country", v)}
                  placeholder="Choose country..."
                />
              </div>
              <div>
                <Label>Postal code *</Label>
                <input
                  className={inputCls}
                  value={form.postal_code || ""}
                  onChange={(e) => setField("postal_code", e.target.value)}
                  required
                />
              </div>
            </div>

            {audience === "patient" && (
              <div className="pt-2 border-t border-[var(--light-border)] space-y-4">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                  <div>
                    <h2 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-xl">
                      Your conditions
                    </h2>
                    <p className="text-[13px] text-[#475569] mt-1 max-w-xl">
                      Add or update every condition you'd like to be considered
                      for. The more we know, the better we can match you to
                      relevant studies.
                    </p>
                  </div>
                  <span className="text-[12px] uppercase tracking-[0.14em] font-semibold text-[var(--text-muted)]">
                    {(form.conditions || []).length}{" "}
                    {(form.conditions || []).length === 1
                      ? "condition"
                      : "conditions"}
                  </span>
                </div>

                {(form.conditions || []).map((cond, idx) => (
                  <ConditionCard
                    key={idx}
                    idx={idx}
                    cond={cond}
                    setCondition={setCondition}
                    removeCondition={removeCondition}
                    conditionChoices={conditionChoices}
                    popular={options.popular_conditions}
                    canRemove={(form.conditions || []).length > 1}
                  />
                ))}

                <button
                  type="button"
                  onClick={addCondition}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl border-2 border-dashed border-[var(--lime)]/60 bg-white text-[var(--charcoal)] font-semibold text-[14px] hover:bg-[var(--lime)]/8 transition-all"
                  data-testid="account-add-condition-btn"
                >
                  <Plus size={16} strokeWidth={2.5} />
                  Add another condition
                </button>
              </div>
            )}

            {audience === "caregiver" && (
              <p className="text-[12.5px] text-[#475569] leading-relaxed border-t border-[var(--light-border)] pt-5">
                Need to change the people you care for or their conditions? You
                can edit those at sign-up but the magic-link page keeps the
                basics. For other changes, contact{" "}
                <a
                  href="mailto:hello@meditalk.world"
                  className="text-[var(--charcoal)] font-semibold underline decoration-[var(--lime)] decoration-2 underline-offset-4"
                >
                  hello@meditalk.world
                </a>
                .
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-semibold text-sm hover:bg-[var(--lime-dark)] transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_8px_22px_-10px_rgba(196,247,75,0.6)]"
                data-testid="account-save-btn"
              >
                {saving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    Save my changes
                    <Check size={16} strokeWidth={2.5} />
                  </>
                )}
              </button>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-[var(--charcoal)] font-medium text-sm border border-[var(--light-border)] hover:bg-white transition-colors"
              >
                Cancel
              </Link>
            </div>
          </form>

          <DangerZone token={token} firstName={form.first_name || ""} />
        </div>
      </section>
    </div>
  );
}

const DangerZone = ({ token, firstName }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onDelete = async () => {
    if (confirmText.trim().toLowerCase() !== "delete") return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/account/me/delete`, { token, confirm: true });
      navigate("/account/deleted", { replace: true });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Couldn't delete your account.";
      toast.error(detail);
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-10 rounded-2xl border border-red-200/70 bg-red-50/40 p-5 md:p-6" data-testid="account-danger-zone">
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-red-100 text-red-600 shrink-0">
          <AlertTriangle size={16} />
        </span>
        <div className="flex-1">
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold text-red-600 mb-1">
            Danger zone
          </span>
          <h2 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-lg leading-tight">
            Delete my MediTalk account
          </h2>
          <p className="text-[13.5px] text-[var(--text-muted)] leading-relaxed mt-1">
            Removes your name, contact details and{" "}
            {firstName ? `${firstName.split(" ")[0]}'s ` : ""}health information
            from MediTalk. You'll receive a confirmation email.{" "}
            <Link
              to="/privacy"
              className="text-[var(--charcoal)] font-semibold underline decoration-red-300 decoration-2 underline-offset-2"
            >
              What we keep & why
            </Link>
            .
          </p>

          {!open && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-red-700 font-semibold text-[13px] border border-red-200 hover:bg-red-50 hover:border-red-300 transition-all"
              data-testid="account-delete-open"
            >
              <Trash2 size={14} />
              Delete my account
            </button>
          )}

          {open && (
            <div className="mt-4 rounded-xl border border-red-200 bg-white p-4">
              <p className="text-[13.5px] text-[var(--charcoal)] mb-3 leading-relaxed">
                This action <strong>cannot be undone</strong>. To confirm, type{" "}
                <span className="font-mono text-red-700 font-bold">DELETE</span>{" "}
                below.
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE to confirm"
                aria-label="Type DELETE to confirm account deletion"
                className="w-full h-11 px-3 rounded-lg border border-red-200 bg-white text-[14px] text-[var(--charcoal)] font-mono focus:outline-none focus:border-red-400 focus:ring-4 focus:ring-red-100"
                data-testid="account-delete-confirm-input"
              />
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={
                    submitting ||
                    confirmText.trim().toLowerCase() !== "delete"
                  }
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-red-600 text-white font-semibold text-[13px] hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed transition-colors"
                  data-testid="account-delete-confirm"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Deleting…
                    </>
                  ) : (
                    <>
                      <Trash2 size={14} />
                      Yes, delete my account
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setConfirmText("");
                  }}
                  disabled={submitting}
                  className="inline-flex items-center justify-center px-4 py-2.5 rounded-full bg-white text-[var(--charcoal)] font-semibold text-[13px] border border-[var(--light-border)] hover:bg-[var(--bone-soft)] transition-colors"
                  data-testid="account-delete-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ConditionCard = ({
  idx,
  cond,
  setCondition,
  removeCondition,
  conditionChoices,
  popular,
  canRemove,
}) => {
  return (
    <div
      className="rounded-2xl border border-[var(--light-border)] bg-white p-5 md:p-6 space-y-4"
      data-testid={`account-condition-${idx}`}
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-semibold text-[var(--charcoal)]">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--lime)]" />
          Condition #{idx + 1}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={() => removeCondition(idx)}
            className="inline-flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-red-600 transition-colors"
            data-testid={`account-remove-condition-${idx}`}
          >
            <Trash2 size={14} /> Remove
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>Condition *</Label>
          <Combobox
            options={conditionChoices}
            value={cond.condition_label || ""}
            onChange={(v) => setCondition(idx, "condition_label", v)}
            placeholder="Type to search 200+ conditions…"
            pinnedValues={popular || []}
            pinnedLabel="Most common"
            data-testid={`account-condition-${idx}-label`}
          />
        </div>

        {cond.condition_label === "Other" && (
          <div className="md:col-span-2">
            <Label>Please specify *</Label>
            <input
              className="w-full h-12 px-4 rounded-xl border border-[var(--light-border)] bg-white text-[15px] text-[var(--charcoal)] focus:outline-none focus:border-[var(--lime)] focus:ring-4 focus:ring-[var(--lime)]/20"
              value={cond.specify || ""}
              onChange={(e) => setCondition(idx, "specify", e.target.value)}
              placeholder="e.g. Immune Thrombocytopenia Purpura"
              data-testid={`account-condition-${idx}-specify`}
            />
          </div>
        )}

        <div>
          <Label>Approx. diagnosis date</Label>
          <MonthYearPicker
            value={cond.diagnosis_date || ""}
            onChange={(v) => setCondition(idx, "diagnosis_date", v)}
            data-testid={`account-condition-${idx}-diagnosis`}
          />
        </div>

        <div>
          <Label>Severity</Label>
          <SegmentedControl
            options={["Mild", "Moderate", "Severe"]}
            value={cond.severity || ""}
            onChange={(v) => setCondition(idx, "severity", v)}
            ariaLabel="Severity"
            data-testid={`account-condition-${idx}-severity`}
          />
        </div>

        <div className="md:col-span-2">
          <Label>Treatment</Label>
          <input
            className="w-full h-12 px-4 rounded-xl border border-[var(--light-border)] bg-white text-[15px] text-[var(--charcoal)] focus:outline-none focus:border-[var(--lime)] focus:ring-4 focus:ring-[var(--lime)]/20"
            value={cond.treatment || ""}
            onChange={(e) => setCondition(idx, "treatment", e.target.value)}
            placeholder="e.g. Antihistamines, biologics, surgery, none"
            data-testid={`account-condition-${idx}-treatment`}
          />
        </div>

        <div className="md:col-span-2">
          <Label>Anything else we should know? (optional)</Label>
          <textarea
            rows={2}
            className="w-full px-4 py-3 rounded-xl border border-[var(--light-border)] bg-white text-[15px] text-[var(--charcoal)] focus:outline-none focus:border-[var(--lime)] focus:ring-4 focus:ring-[var(--lime)]/20 resize-y"
            value={cond.other_info || ""}
            onChange={(e) => setCondition(idx, "other_info", e.target.value)}
            placeholder="Anything that might affect study eligibility"
            data-testid={`account-condition-${idx}-other`}
          />
        </div>
      </div>
    </div>
  );
};

const LinkBrokenState = ({ code, message }) => {
  const isExpired = code === 410 || code === 404;
  return (
    <div className="bg-white min-h-screen">
      <section className="bg-[var(--dark-bg)] grain-dark py-16 md:py-24 relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/10 blur-3xl pointer-events-none"
        />
        <div className="container-main max-w-xl text-center relative z-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/[0.08] border border-white/15 mb-6">
            <ShieldCheck size={28} className="text-[var(--lime)]" />
          </div>
          <h1 className="font-['Outfit'] font-bold text-white text-3xl md:text-4xl leading-tight mb-4">
            {isExpired ? "This link has expired" : "Something went wrong"}
          </h1>
          <p className="text-white/80 leading-relaxed mb-8 max-w-md mx-auto">
            {message ||
              "We could not validate this link. Please request a new one and try again."}
          </p>
          <Link
            to="/account/request-link"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-semibold text-sm hover:bg-[var(--lime-dark)] transition-all"
          >
            Request a new link
          </Link>
        </div>
      </section>
    </div>
  );
};
